import { useState, useMemo, useCallback, useRef } from "react";
import { useQueryClient } from "react-query";
import {
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import jrwaService, { EJrwaSeedType, jrwaQueryKeys } from "api/jrwaService";
import workspaceService from "api/workspaceService";
import { IError } from "dto/IError";
import { IWorkspaceMember } from "dto/IWorkspace";
import useCurrentCompanyId from "hooks/useCurrentCompanyId";
import { ENotificationType, notification } from "utils/notification";

import {
  ARCHIVE_TYPES,
  archiveTypeLabel,
  buildJrwaTree,
  IJrwaNode,
} from "./jrwaTree";

import styles from "./styles.module.scss";

// Jednolity rzeczowy wykaz akt — port klasyfikacji z Magistratu. Wykaz jest w bazie
// (per firma, GET /jrwa; pusty zasilany oficjalnym JRWA gminy), a nie w localStorage.
// Pozycje oficjalne (IsReadOnly) mają niezmienny kod i hasło — można im nadać
// kategorię archiwalną albo zarchiwizować; własne rozszerzenia są w pełni edytowalne
// i usuwalne, dopóki nie mają teczek.
//
// Prowadzący kod + „Pracownicy prowadzą teczki wspólnie" (port metryki usługi z Magistratu:
// Procedure.UserIds / IsShared). Teczka jest PRYWATNA (widzi ją tylko właściciel), chyba że
// jej kod jest prowadzony wspólnie — wtedy każdy prowadzący wskazany przy kodzie widzi
// teczkę i ma do niej pełny dostęp. Regułę egzekwuje BE (BaseRepository.CaseFullAccess).

/** Aktywni członkowie firmy do wyboru jako prowadzący (WorkspaceMemberStatus.Active = 4). */
const activeMembers = (members: IWorkspaceMember[] | undefined) =>
  (members ?? [])
    .filter((m) => !!m.UserId && m.Status === 4)
    .sort((a, b) => a.Label.localeCompare(b.Label, "pl"));

const collectAllIds = (nodes: IJrwaNode[]): Set<string> => {
  const ids = new Set<string>();
  const visit = (n: IJrwaNode) => {
    ids.add(n.id);
    n.children.forEach(visit);
  };
  nodes.forEach(visit);
  return ids;
};

const filterTree = (nodes: IJrwaNode[], q: string): [IJrwaNode[], Set<string>] => {
  const lower = q.toLowerCase();
  const expandIds = new Set<string>();

  const filterNode = (n: IJrwaNode): IJrwaNode | null => {
    const kids = n.children
      .map(filterNode)
      .filter(Boolean) as IJrwaNode[];
    const selfMatch =
      n.code.toLowerCase().includes(lower) ||
      n.label.toLowerCase().includes(lower);
    if (selfMatch || kids.length > 0) {
      if (kids.length > 0) expandIds.add(n.id);
      return { ...n, children: selfMatch ? n.children : kids };
    }
    return null;
  };

  const result = nodes.map(filterNode).filter(Boolean) as IJrwaNode[];
  return [result, expandIds];
};

// ── TreeNode ──────────────────────────────────────────────────────────────────

interface TreeNodeProps {
  node: IJrwaNode;
  depth: number;
  expanded: Set<string>;
  canEdit: boolean;
  /** Etykiety członków po UserId — do podpisu „prowadzący: …" przy kodzie. */
  memberLabels: Map<string, string>;
  onToggle: (id: string) => void;
  onAdd: (node: IJrwaNode) => void;
  onEdit: (node: IJrwaNode) => void;
  onDelete: (node: IJrwaNode) => void;
  searchActive: boolean;
}

const TreeNode = ({
  node,
  depth,
  expanded,
  canEdit,
  memberLabels,
  onToggle,
  onAdd,
  onEdit,
  onDelete,
  searchActive,
}: TreeNodeProps) => {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const leaders = node.userIds.map((id) => memberLabels.get(id) ?? "?");

  return (
    <div className={styles.nodeWrapper}>
      <div
        className={`${styles.nodeRow} ${depth === 0 ? styles.nodeRowL1 : ""} ${
          depth === 1 ? styles.nodeRowL2 : ""
        }`}
        style={{ paddingLeft: 8 + depth * 20, opacity: node.isArchived ? 0.5 : 1 }}
      >
        <span
          className={styles.chevron}
          onClick={() => hasChildren && onToggle(node.id)}
          style={{ visibility: hasChildren ? "visible" : "hidden" }}
        >
          {isOpen || searchActive ? (
            <ExpandMoreIcon sx={{ fontSize: 16 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          )}
        </span>

        <span className={`${styles.codeChip} ${node.custom ? styles.codeChipCustom : ""}`}>
          {node.code}
        </span>

        <span className={`${styles.nodeLabel} ${node.custom ? styles.nodeLabelCustom : ""}`}>
          {node.label}
          {node.archiveType ? (
            <span style={{ marginLeft: 8, fontSize: 11, color: "rgba(0,0,0,0.45)" }}>
              kat. {archiveTypeLabel(node.archiveType)}
            </span>
          ) : null}
          {node.casesCount > 0 && (
            <span style={{ marginLeft: 8, fontSize: 11, color: "rgba(0,0,0,0.45)" }}>
              teczek: {node.casesCount}
            </span>
          )}
          {node.isArchived && (
            <span style={{ marginLeft: 8, fontSize: 11, color: "rgba(0,0,0,0.45)" }}>
              (zarchiwizowana)
            </span>
          )}
          {leaders.length > 0 && (
            <span
              style={{ marginLeft: 8, fontSize: 11, color: "rgba(0,0,0,0.45)" }}
              title={`Prowadzący: ${leaders.join(", ")}`}
            >
              prowadzący: {leaders.join(", ")}
            </span>
          )}
          {node.isShared && (
            <Tooltip
              title="Pracownicy prowadzą teczki wspólnie — każdy prowadzący widzi teczki pod tym kodem i ma do nich pełny dostęp"
              placement="top"
              arrow
            >
              <span className={styles.sharedChip}>wspólnie</span>
            </Tooltip>
          )}
        </span>

        {canEdit && (
          <span className={styles.nodeActions}>
            <Tooltip title="Dodaj podkategorię" placement="top" arrow>
              <span
                className={styles.actionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onAdd(node);
                }}
              >
                <AddIcon sx={{ fontSize: 13 }} />
              </span>
            </Tooltip>
            <Tooltip
              title={node.custom ? "Edytuj pozycję" : "Prowadzący / kategoria archiwalna / archiwizacja"}
              placement="top"
              arrow
            >
              <span
                className={styles.actionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(node);
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 13 }} />
              </span>
            </Tooltip>
            {node.custom && (
              <Tooltip title="Usuń" placement="top" arrow>
                <span
                  className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(node);
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                </span>
              </Tooltip>
            )}
          </span>
        )}
      </div>

      {hasChildren && (isOpen || searchActive) && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              canEdit={canEdit}
              memberLabels={memberLabels}
              onToggle={onToggle}
              onAdd={onAdd}
              onEdit={onEdit}
              onDelete={onDelete}
              searchActive={searchActive}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Prowadzący + „wspólnie" (pola wspólne dla dialogów dodawania i edycji) ────

interface LeadersFieldsProps {
  members: IWorkspaceMember[];
  userIds: string[];
  isShared: boolean;
  onUserIdsChange: (ids: string[]) => void;
  onSharedChange: (v: boolean) => void;
}

const LeadersFields = ({
  members,
  userIds,
  isShared,
  onUserIdsChange,
  onSharedChange,
}: LeadersFieldsProps) => {
  const selected = members.filter((m) => userIds.includes(m.UserId));

  return (
    <>
      <Autocomplete
        multiple
        size="small"
        options={members}
        value={selected}
        getOptionLabel={(m) => m.Label}
        isOptionEqualToValue={(a, b) => a.UserId === b.UserId}
        onChange={(_, value) => onUserIdsChange(value.map((m) => m.UserId))}
        renderTags={(value, getTagProps) =>
          value.map((m, index) => (
            <Chip {...getTagProps({ index })} key={m.UserId} size="small" label={m.Label} />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Pracownicy prowadzący"
            placeholder={selected.length ? "" : "Wybierz członków firmy…"}
            helperText="Osoby odpowiedzialne za prowadzenie teczek pod tym kodem"
          />
        )}
        sx={{ mt: 2 }}
        noOptionsText="Brak członków firmy"
      />
      <FormControlLabel
        control={
          <Checkbox size="small" checked={isShared} onChange={(e) => onSharedChange(e.target.checked)} />
        }
        label="Pracownicy prowadzą teczki wspólnie"
      />
      <div className={styles.dialogHint} style={{ marginTop: 0 }}>
        {isShared
          ? "Każdy z prowadzących widzi wszystkie teczki pod tym kodem i ma do nich pełny dostęp."
          : "Teczki pod tym kodem są prywatne — widzi je wyłącznie właściciel teczki."}
      </div>
    </>
  );
};

// ── Główny komponent ──────────────────────────────────────────────────────────

const errorText = (error: IError, fallback: string) =>
  error?.response?.data?.errorCodes?.[0]?.message ?? fallback;

const JrwaTab = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = jrwaService.useGetList();
  const { mutate: save, isLoading: isSaving } = jrwaService.useSave();
  const { mutate: remove } = jrwaService.useRemove();
  const { mutate: reset, isLoading: isResetting } = jrwaService.useReset();

  const canEdit = data?.data?.CanEdit ?? false;
  const hasCases = data?.data?.HasCases ?? false;

  // Członkowie firmy — prowadzący kodów (jak CaseInfo: Id firmy z kontekstu, nie z mirrora).
  const workspaceId = useCurrentCompanyId();
  const { data: workspaceResponse } = workspaceService.useGetWorkspace(workspaceId);
  const members = useMemo(
    () => activeMembers(workspaceResponse?.data?.Members),
    [workspaceResponse],
  );
  const memberLabels = useMemo(
    () => new Map((workspaceResponse?.data?.Members ?? []).map((m) => [m.UserId, m.Label])),
    [workspaceResponse],
  );

  const tree = useMemo(() => buildJrwaTree(data?.data?.Items ?? []), [data]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  // Dialogi
  const [addDialog, setAddDialog] = useState<IJrwaNode | null>(null);
  const [editDialog, setEditDialog] = useState<IJrwaNode | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<IJrwaNode | null>(null);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetType, setResetType] = useState<EJrwaSeedType>(EJrwaSeedType.Gmina);

  // Pola dialogów
  const [formCode, setFormCode] = useState("");
  const [formLabel, setFormLabel] = useState("");
  const [formArchiveType, setFormArchiveType] = useState<number>(0);
  const [formArchived, setFormArchived] = useState(false);
  const [formShared, setFormShared] = useState(false);
  const [formUserIds, setFormUserIds] = useState<string[]>([]);

  const refresh = () => queryClient.invalidateQueries(jrwaQueryKeys.list);

  const [filteredTree, searchExpanded] = useMemo(() => {
    if (!search.trim()) return [tree, new Set<string>()] as const;
    return filterTree(tree, search.trim());
  }, [tree, search]);

  const searchActive = search.trim().length > 0;

  const handleToggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleExpandAll = () => setExpanded(collectAllIds(tree));
  const handleCollapseAll = () => setExpanded(new Set());

  // ── Dodanie ───────────────────────────────────────────────────────────────

  const openAdd = (parent: IJrwaNode) => {
    setAddDialog(parent);
    setFormCode(parent.code);
    setFormLabel("");
    setFormArchiveType(0);
    setFormShared(false);
    setFormUserIds([]);
  };

  const confirmAdd = () => {
    if (!addDialog || !formLabel.trim() || !formCode.trim()) return;
    save(
      {
        ParentId: addDialog.id,
        Code: formCode.trim(),
        Description: formLabel.trim(),
        ArchiveType: formArchiveType || null,
        IsShared: formShared,
        UserIds: formUserIds,
      },
      {
        onSuccess: () => {
          setExpanded((prev) => new Set([...prev, addDialog.id]));
          setAddDialog(null);
          refresh();
        },
        onError: (error) =>
          notification({
            type: ENotificationType.ERROR,
            title: "JRWA",
            text: errorText(error, "Nie udało się dodać pozycji"),
          }),
      },
    );
  };

  // ── Edycja ────────────────────────────────────────────────────────────────

  const openEdit = (node: IJrwaNode) => {
    setEditDialog(node);
    setFormCode(node.code);
    setFormLabel(node.label);
    setFormArchiveType(node.archiveType ?? 0);
    setFormArchived(node.isArchived);
    setFormShared(node.isShared);
    setFormUserIds(node.userIds);
  };

  const confirmEdit = () => {
    if (!editDialog || !formLabel.trim() || !formCode.trim()) return;
    save(
      {
        Id: editDialog.id,
        ParentId: editDialog.parentId ?? null,
        Code: editDialog.custom ? formCode.trim() : editDialog.code,
        Description: editDialog.custom ? formLabel.trim() : editDialog.label,
        ArchiveType: formArchiveType || null,
        IsArchived: formArchived,
        IsShared: formShared,
        UserIds: formUserIds,
      },
      {
        onSuccess: () => {
          setEditDialog(null);
          refresh();
        },
        onError: (error) =>
          notification({
            type: ENotificationType.ERROR,
            title: "JRWA",
            text: errorText(error, "Nie udało się zapisać pozycji"),
          }),
      },
    );
  };

  // ── Usunięcie ─────────────────────────────────────────────────────────────

  const confirmDelete = () => {
    if (!deleteConfirm) return;
    remove(
      { id: deleteConfirm.id, cascade: true },
      {
        onSuccess: () => {
          setDeleteConfirm(null);
          refresh();
        },
        onError: (error) =>
          notification({
            type: ENotificationType.ERROR,
            title: "JRWA",
            text: errorText(error, "Nie udało się usunąć pozycji"),
          }),
      },
    );
  };

  // ── Wymiana wykazu ────────────────────────────────────────────────────────

  const confirmReset = () => {
    reset(resetType, {
      onSuccess: () => {
        setResetDialog(false);
        setExpanded(new Set());
        refresh();
      },
      onError: (error) =>
        notification({
          type: ENotificationType.ERROR,
          title: "JRWA",
          text: errorText(error, "Nie udało się wymienić wykazu"),
        }),
    });
  };

  const displayTree = searchActive ? filteredTree : tree;
  const displayExpanded = searchActive ? searchExpanded : expanded;

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <TextField
          inputRef={searchRef}
          size="small"
          placeholder="Szukaj po symbolu lub haśle klasyfikacyjnym…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchBox}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  ✕
                </IconButton>
              </InputAdornment>
            ) : (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.3)" }} />
              </InputAdornment>
            ),
          }}
        />
        <div className={styles.toolbarRight}>
          <Tooltip title="Rozwiń wszystko">
            <span>
              <IconButton size="small" onClick={handleExpandAll} disabled={searchActive}>
                <UnfoldMoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zwiń wszystko">
            <span>
              <IconButton size="small" onClick={handleCollapseAll} disabled={searchActive}>
                <UnfoldLessIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {canEdit && (
            <Tooltip
              title={
                hasCases
                  ? "Wymiana wykazu niemożliwa — istnieją teczki z kodem JRWA"
                  : "Wymień wykaz na oficjalny (gmina / powiat / województwo) — usuwa własne rozszerzenia"
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => setResetDialog(true)}
                  disabled={hasCases}
                  sx={{ color: "rgba(0,0,0,0.35)" }}
                >
                  <span style={{ fontSize: 11, fontWeight: 600 }}>↺</span>
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
      </div>

      <div className={styles.legend}>
        <span className={`${styles.legendDot} ${styles.legendDotSystem}`} />
        oficjalne JRWA
        <span className={`${styles.legendDot} ${styles.legendDotCustom}`} style={{ marginLeft: 16 }} />
        własne rozszerzenia
        <span className={styles.legendHint}>
          — wykaz firmy w bazie; kod JRWA nadaje teczce sygnaturę SYMBOL.KOD.NR.ROK; teczki są
          prywatne, chyba że kod jest prowadzony wspólnie (wtedy widzą je wszyscy prowadzący)
          {canEdit ? "; ikony akcji pojawiają się po najechaniu na wiersz" : ""}
        </span>
      </div>

      <div className={styles.treeContainer}>
        {isLoading ? (
          <div className={styles.emptyMsg}>Ładowanie wykazu…</div>
        ) : displayTree.length === 0 ? (
          <div className={styles.emptyMsg}>
            {searchActive ? `Brak wyników dla „${search}"` : "Wykaz jest pusty"}
          </div>
        ) : (
          displayTree.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              depth={0}
              expanded={displayExpanded}
              canEdit={canEdit}
              memberLabels={memberLabels}
              onToggle={handleToggle}
              onAdd={openAdd}
              onEdit={openEdit}
              onDelete={setDeleteConfirm}
              searchActive={searchActive}
            />
          ))
        )}
      </div>

      {/* Dodaj podkategorię */}
      <Dialog open={!!addDialog} onClose={() => setAddDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>Dodaj podkategorię</DialogTitle>
        <DialogContent>
          <div className={styles.dialogHint}>
            Kategoria nadrzędna: <strong>{addDialog?.code} - {addDialog?.label}</strong>
          </div>
          <TextField
            label="Symbol klasyfikacyjny *"
            size="small"
            fullWidth
            value={formCode}
            onChange={(e) => setFormCode(e.target.value)}
            sx={{ mt: 1.5, mb: 2 }}
            placeholder="np. 00021"
            helperText="Symbol musi być unikalny w wykazie firmy"
            inputProps={{ maxLength: 20 }}
          />
          <TextField
            label="Hasło klasyfikacyjne *"
            size="small"
            fullWidth
            value={formLabel}
            onChange={(e) => setFormLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmAdd()}
            autoFocus
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label="Kategoria archiwalna"
            size="small"
            fullWidth
            value={formArchiveType}
            onChange={(e) => setFormArchiveType(Number(e.target.value))}
          >
            {ARCHIVE_TYPES.map((a) => (
              <MenuItem key={a.value} value={a.value}>
                {a.label}
              </MenuItem>
            ))}
          </TextField>
          <LeadersFields
            members={members}
            userIds={formUserIds}
            isShared={formShared}
            onUserIdsChange={setFormUserIds}
            onSharedChange={setFormShared}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialog(null)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button
            onClick={confirmAdd}
            variant="contained"
            size="small"
            disabled={!formLabel.trim() || !formCode.trim() || isSaving}
          >
            Dodaj
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edytuj */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          {editDialog?.custom ? "Edytuj pozycję" : "Pozycja oficjalnego wykazu"}
        </DialogTitle>
        <DialogContent>
          {!editDialog?.custom && (
            <div className={styles.dialogHint}>
              Symbol i hasło pozycji oficjalnej są niezmienne — możesz nadać kategorię
              archiwalną, zarchiwizować pozycję (nie będzie dostępna dla nowych teczek)
              oraz wskazać prowadzących i sposób prowadzenia teczek.
            </div>
          )}
          <TextField
            label="Symbol klasyfikacyjny *"
            size="small"
            fullWidth
            value={formCode}
            disabled={!editDialog?.custom}
            onChange={(e) => setFormCode(e.target.value)}
            sx={{ mt: 1.5, mb: 2 }}
            inputProps={{ maxLength: 20 }}
          />
          <TextField
            label="Hasło klasyfikacyjne *"
            size="small"
            fullWidth
            value={formLabel}
            disabled={!editDialog?.custom}
            onChange={(e) => setFormLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmEdit()}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label="Kategoria archiwalna"
            size="small"
            fullWidth
            value={formArchiveType}
            onChange={(e) => setFormArchiveType(Number(e.target.value))}
            sx={{ mb: 1 }}
          >
            {ARCHIVE_TYPES.map((a) => (
              <MenuItem key={a.value} value={a.value}>
                {a.label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={formArchived}
                onChange={(e) => setFormArchived(e.target.checked)}
              />
            }
            label="Zarchiwizowana (niedostępna dla nowych teczek)"
          />
          <LeadersFields
            members={members}
            userIds={formUserIds}
            isShared={formShared}
            onUserIdsChange={setFormUserIds}
            onSharedChange={setFormShared}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialog(null)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button
            onClick={confirmEdit}
            variant="contained"
            size="small"
            disabled={!formLabel.trim() || !formCode.trim() || isSaving}
          >
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usuń */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>Usuń pozycję</DialogTitle>
        <DialogContent>
          Czy na pewno usunąć pozycję{" "}
          <strong>„{deleteConfirm?.code} - {deleteConfirm?.label}"</strong>?
          <br />
          <span style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginTop: 4, display: "block" }}>
            Zostaną usunięte również jej pozycje podrzędne. Pozycje z teczkami, oficjalne
            i zarchiwizowane nie zostaną usunięte.
          </span>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error" size="small">
            Usuń
          </Button>
        </DialogActions>
      </Dialog>

      {/* Wymiana wykazu */}
      <Dialog open={resetDialog} onClose={() => setResetDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>Wymień wykaz akt</DialogTitle>
        <DialogContent>
          <div className={styles.dialogHint}>
            Cały wykaz firmy zostanie zastąpiony oficjalnym JRWA wybranego rodzaju jednostki.
            Własne rozszerzenia przepadną.
          </div>
          <TextField
            select
            label="Rodzaj jednostki"
            size="small"
            fullWidth
            value={resetType}
            onChange={(e) => setResetType(Number(e.target.value) as EJrwaSeedType)}
            sx={{ mt: 1.5 }}
          >
            <MenuItem value={EJrwaSeedType.Gmina}>Gmina</MenuItem>
            <MenuItem value={EJrwaSeedType.Powiat}>Powiat</MenuItem>
            <MenuItem value={EJrwaSeedType.Wojewodztwo}>Województwo</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setResetDialog(false)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button onClick={confirmReset} variant="contained" color="error" size="small" disabled={isResetting}>
            Wymień
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default JrwaTab;
